import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

const COLUMNS = [
  { kicker: "People", body: "Lead and support the Member Engagement Team across frontline member support, membership administration and service delivery. Clearer work allocation, stronger operational visibility, supportive team environment." },
  { kicker: "Systems", body: "Oversee and improve the branch CRM and digital workflows. Connected processes across iMIS, Microsoft 365, SharePoint, Teams, Zoom Phone and automation to reduce unnecessary manual administration." },
  { kicker: "Impact", body: "Improve member experience, service consistency and operational visibility. Use data, reporting and workflow analysis to identify trends, reduce bottlenecks and support member engagement, retention and growth." },
];

const CAPABILITIES = [
  "People Leadership", "Member Experience", "Digital Transformation",
  "CRM & iMIS", "Workflow Design", "Automation",
  "Microsoft 365", "Operational Improvement", "Data & Reporting",
];

export default function CurrentRole() {
  return (
    <section
      className="bg-[#0B0B0C] border-t border-b border-[#343437] relative grain"
      data-testid="home-current-role"
      aria-labelledby="home-current-role-title"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-14 lg:py-18">
        {/* Metadata + status pulse — whitespace-nowrap kills the ACTIVEENGAGEMENT bug */}
        <div className="flex items-center gap-4 flex-wrap mb-5">
          <p className="eyebrow whitespace-nowrap">Current chapter · Sep 2026 — Present</p>
          <span className="w-px h-4 bg-[#343437] hidden sm:block" aria-hidden />
          <span className="inline-flex items-center gap-2 whitespace-nowrap text-[10px] tracking-[0.22em] uppercase text-[#D8A936] font-display">
            <span className="w-1.5 h-1.5 rounded-full bg-[#D8A936] animate-pulse" aria-hidden />
            Active engagement
          </span>
        </div>

        {/* Editorial title + ASU logo lockup */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 mb-8 lg:mb-10">
          <div className="lg:col-span-8">
            <motion.h2
              id="home-current-role-title"
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6 }}
              className="font-display leading-[1.02] tracking-[-0.02em] text-[#FAF9F6]"
              style={{ fontSize: "clamp(1.75rem, 3.6vw, 3.1rem)" }}
            >
              Member Engagement <span className="text-[#D8A936]">&</span> Digital Transformation Manager
            </motion.h2>

            <div className="mt-5 flex items-center gap-5 flex-wrap">
              {/* Restrained ASU logo — height-capped so it never dominates */}
              <img
                src="/assets/asu-logo.png"
                alt="Australian Services Union — Victorian & Tasmanian Authorities & Services Branch"
                className="h-11 w-auto opacity-90 shrink-0"
                loading="lazy"
                data-testid="asu-logo"
              />
              <div className="min-w-0">
                <p className="font-display text-base md:text-lg text-[#FAF9F6]">Australian Services Union</p>
                <p className="text-[11.5px] tracking-[0.14em] uppercase text-[#A9A9A6] font-display">
                  Victorian &amp; Tasmanian Authorities &amp; Services Branch · Melbourne
                </p>
              </div>
            </div>
          </div>

          <div className="lg:col-span-4 lg:pt-3">
            <p className="text-[#A9A9A6] leading-relaxed text-[14.5px]">
              Leading the people, systems and workflows behind frontline member engagement across Victoria and Tasmania — connecting people, processes, CRM and technology to improve how members are supported.
            </p>
          </div>
        </div>

        {/* Three tight editorial columns */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-10 border-t border-[#343437] pt-9">
          {COLUMNS.map((c, i) => (
            <motion.article
              key={c.kicker}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.55, delay: i * 0.08 }}
              className="relative"
              data-testid={`current-role-${c.kicker.toLowerCase()}`}
            >
              <span aria-hidden className="block w-8 h-px bg-[#D8A936] mb-4" />
              <p className="font-display text-[11px] tracking-[0.28em] uppercase text-[#D8A936] mb-3">
                {c.kicker}
              </p>
              <p className="text-[#A9A9A6] leading-relaxed text-[14.5px]">{c.body}</p>
            </motion.article>
          ))}
        </div>

        {/* Capabilities + CTA */}
        <div className="mt-9 border-t border-[#343437] pt-6">
          <div className="flex items-start justify-between gap-6 flex-wrap">
            <div className="flex-1 min-w-[280px]">
              <p className="eyebrow mb-3">Working across</p>
              <div className="flex flex-wrap gap-x-5 gap-y-2">
                {CAPABILITIES.map((c) => (
                  <span
                    key={c}
                    className="font-display text-[11px] tracking-[0.14em] uppercase text-[#FAF9F6]/85"
                  >
                    {c}
                  </span>
                ))}
              </div>
            </div>
            <Link
              to="/about"
              data-testid="home-current-role-cta"
              className="inline-flex items-center gap-2 text-[#D8A936] font-display text-[11.5px] tracking-[0.18em] uppercase border-b border-[#D8A936]/40 pb-1 hover:border-[#D8A936] transition-colors self-end"
            >
              Explore my current role <ArrowUpRight size={13} />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
